// Was von einer Spur tatsächlich klingt: Der obere (später angelegte) Clip verdeckt den unteren wie in Pro Tools
// (Layered Editing), außer in seinen Fade-Bereichen, damit Crossfades funktionieren. Stumme Clips klingen nicht,
// verdecken aber. Clip-Loops wiederholen ihren Quellausschnitt. Live-Wiedergabe und Export nutzen dieselben Abschnitte.
import type { Clip, FadeShape, Note, Project, TempoSource } from '@srs/shared';
import { beatAt, secAt } from '@srs/shared';

const end = (c: Clip) => c.start + c.duration;
export const dbToLin = (db: number) => Math.pow(10, db / 20);

/** Verstärkung eines Fade-Ins bei x = 0..1 (Fade-Out: Kurve gespiegelt) */
export function fadeCurve(shape: FadeShape | undefined, x: number): number {
  x = Math.max(0, Math.min(1, x));
  switch (shape) {
    case 'linear': return x;
    case 'equalPower': return Math.sin((x * Math.PI) / 2);
    case 'exp': return x * x;
    case 'log': return 1 - (1 - x) * (1 - x);
    default: return (1 - Math.cos(Math.PI * x)) / 2; // S-Kurve
  }
}
/** Gesamtverstärkung eines Clips zur Timeline-Zeit t (Clip Gain × Fades) */
export function clipGainAt(c: Clip, t: number): number {
  let g = dbToLin(c.gainDb ?? 0);
  if (c.fadeIn > 0 && t < c.start + c.fadeIn) g *= fadeCurve(c.fadeInShape, (t - c.start) / c.fadeIn);
  if (c.fadeOut > 0 && t > end(c) - c.fadeOut) g *= fadeCurve(c.fadeOutShape, (end(c) - t) / c.fadeOut);
  return g;
}

export interface Segment { clip: Clip; from: number; to: number; /** Quellzeit (s) bei from */ src: number; /** gerechneter Puffer (Elastic Audio) statt clip.sourceId */ sourceId?: string }

/** Elastic Audio: liefert für gedehnte Clips den gerechneten Puffer (audio/elastic.ts trägt sich hier ein) */
type ElasticResolver = (p: Project, c: Clip) => { id: string; src0: number } | 'pending' | null;
let elasticResolver: ElasticResolver | null = null;
export const setElasticResolver = (r: ElasticResolver) => { elasticResolver = r; };

function subtract(parts: [number, number][], s: number, e: number): [number, number][] {
  const out: [number, number][] = [];
  for (const [a, b] of parts) {
    if (e <= a || s >= b) { out.push([a, b]); continue; }
    if (s > a) out.push([a, s]);
    if (e < b) out.push([e, b]);
  }
  return out.filter(([a, b]) => b - a > 1e-6);
}

export function audioSegments(p: Project, trackId: string): Segment[] {
  const clips = p.clips.filter((c) => c.trackId === trackId && c.kind === 'audio' && c.sourceId);
  const out: Segment[] = [];
  clips.forEach((c, i) => {
    if (c.muted) return;
    let parts: [number, number][] = [[c.start, end(c)]];
    for (const u of clips.slice(i + 1)) {
      const s = u.start + u.fadeIn, e = end(u) - u.fadeOut;
      if (e - s > 1e-6) parts = subtract(parts, s, e);
    }
    const el = elasticResolver?.(p, c) ?? null;
    if (el === 'pending') return;   // wird gerade gerechnet: kurz still, verdeckt aber darunterliegende Clips
    for (const [a, b] of parts) {
      if (el) { out.push({ clip: c, from: a, to: b, src: el.src0 + (a - c.start), sourceId: el.id }); continue; }
      if (c.loop && c.loop.length > 0) {
        const len = c.loop.length;
        for (let k = Math.floor((a - c.start) / len); c.start + k * len < b - 1e-9; k++) {
          const is = c.start + k * len, s = Math.max(a, is), e = Math.min(b, is + len);
          if (e - s > 1e-6) out.push({ clip: c, from: s, to: e, src: c.offset + (s - is) });
        }
      } else out.push({ clip: c, from: a, to: b, src: c.offset + (a - c.start) });
    }
  });
  return out;
}

/**
 * Lautstärkeverlauf eines Abschnitts auf einen GainNode schreiben: Fade-Bereiche als Kurve, dazwischen konstant.
 * toCtx rechnet Timeline-Zeit in Kontextzeit um. Spielt ab segStart (kann mitten im Abschnitt liegen).
 */
export function applyEnvelope(g: GainNode, c: Clip, segStart: number, segEnd: number, toCtx: (t: number) => number) {
  const base = dbToLin(c.gainDb ?? 0);
  const fiEnd = c.start + c.fadeIn, foStart = end(c) - c.fadeOut;
  const curve = (a: number, b: number) => {
    const n = Math.max(2, Math.min(256, Math.ceil((b - a) * 400)));
    return Float32Array.from({ length: n }, (_, i) => clipGainAt(c, a + ((b - a) * i) / (n - 1)));
  };
  const regions: { a: number; b: number; fade: boolean }[] = [];
  let t = segStart;
  if (c.fadeIn > 0 && t < fiEnd) { const b = Math.min(segEnd, fiEnd); regions.push({ a: t, b, fade: true }); t = b; }
  if (t < segEnd) {
    const mid = c.fadeOut > 0 ? Math.min(segEnd, Math.max(t, foStart)) : segEnd;
    if (mid > t) { regions.push({ a: t, b: mid, fade: false }); t = mid; }
    if (t < segEnd) regions.push({ a: t, b: segEnd, fade: true });
  }
  g.gain.setValueAtTime(clipGainAt(c, segStart), toCtx(segStart));
  for (const r of regions) {
    if (r.b - r.a < 1e-4) continue;
    const at = toCtx(r.a) + 1e-5;
    if (r.fade) g.gain.setValueCurveAtTime(curve(r.a, r.b) as Float32Array<ArrayBuffer>, at, r.b - r.a - 2e-5);
    else g.gain.setValueAtTime(base, at);
  }
}

/** MIDI-Noten eines Clips mit Loop-Durchgängen: absolute Zeit, Dauer, Note. Versteckte Noten (außerhalb) fallen weg. */
export function midiEvents(p: TempoSource, c: Clip, from: number, to: number): { at: number; dur: number; n: Note }[] {
  if (c.muted || !c.notes?.length) return [];
  const len = c.loop?.length && c.loop.length > 0 ? c.loop.length : c.duration;
  const out: { at: number; dur: number; n: Note }[] = [];
  const vel = dbToLin(c.gainDb ?? 0);
  for (let k = 0; k * len < c.duration - 1e-9; k++) {
    const is = c.start + k * len, ie = Math.min(end(c), is + len);
    if (ie <= from || is >= to) continue;
    // Noten liegen in Vierteln ab Clip-Anfang; die Tempo-Spur rechnet sie in Sekunden um
    const b0 = beatAt(p, is);
    for (const n of c.notes) {
      if (n.t < -1e-9) continue;
      const at = secAt(p, b0 + n.t);
      if (at >= ie - 1e-9 || at < from || at >= to) continue;
      out.push({ at, dur: Math.min(secAt(p, b0 + n.t + n.d) - at, ie - at), n: vel === 1 ? n : { ...n, v: Math.min(1, n.v * vel) } });
    }
  }
  return out;
}
